import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { authedV3Request } from "utils/api/requests";
import { selectAuthToken } from "containers/App/selectors";

/*
 * React Query hooks for the /admin section. All admin reads go through
 * `authedV3Request`, which sets the Rails token-auth header
 * (`Authorization: Token token=<authToken>`) the /api/v3/admin/* endpoints
 * require. The backend returns 403 for non-super_admins regardless of the
 * client-side guard, so these hooks are safe to mount only behind AdminPage.
 *
 * The token is read from Redux at call time. The `enabled` gate prevents a
 * tokenless request before sign-in, and the token is part of every query key
 * so the cache is scoped per-identity — if two super_admins use the same device
 * within the staleTime window, the second doesn't read the first's cached data.
 */

/*
 * Growth metrics — channel / gif / image / share counts. This is the
 * production growth-data hook: GrowthDashboard (/admin/growth) consumes it to
 * render the full dashboard — top-line counts, per-type share breakdowns, and
 * GIFs-by-month.
 */
export const useAdminGrowth = () => {
  const authToken = useSelector(selectAuthToken());
  return useQuery({
    queryFn: () => authedV3Request("v3/admin/growth", authToken),
    queryKey: ["admin", "growth", authToken],
    retry: 0,
    staleTime: 60 * 1000,
    enabled: !!authToken,
  });
};

/*
 * Build the /admin/users query string. The endpoint accepts ONLY page/per_page
 * (the documented search/role params were phantom — never implemented in the
 * controller — and have been removed from the spec), so we send only those.
 */
const buildUsersQuery = ({ page, perPage } = {}) => {
  const params = new URLSearchParams();
  // `!= null` (not truthiness) so an explicit page/per_page of 0 is still sent
  // rather than silently dropped — the caller's intent is honored verbatim.
  if (page != null) params.set("page", String(page));
  if (perPage != null) params.set("per_page", String(perPage));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

/*
 * Paginated user list. The response is a pagination envelope:
 *   { current_page, rows_per_page, page_count, data: [{ id, first_name,
 *     last_name, email }] }
 * Verified against Api::V3::Admin::UsersController#index + the
 * V3ApiConstants::ADMIN_INDEX_USER_SHAPE — the list rows do NOT include
 * permission_role (that's detail-only), so the table can't show role inline.
 * `keepPreviousData` keeps the current page visible while the next loads, so
 * paging doesn't flash an empty table.
 */
export const useAdminUsers = (filters) => {
  const authToken = useSelector(selectAuthToken());
  return useQuery({
    queryFn: () =>
      authedV3Request(`v3/admin/users${buildUsersQuery(filters)}`, authToken),
    queryKey: ["admin", "users", filters, authToken],
    retry: 0,
    staleTime: 30 * 1000,
    keepPreviousData: true,
    enabled: !!authToken,
  });
};

/*
 * Single user detail. Response = ADMIN_GET_USER_SHAPE (id, customer_name,
 * email, expiration_date, first_name, last_name, permission_role [enum string],
 * + subscription/trial fields). Verified against
 * Api::V3::Admin::UsersController#show. `id` is required; the `enabled` gate
 * skips the request when it's missing.
 */
export const useAdminUser = (id) => {
  const authToken = useSelector(selectAuthToken());
  return useQuery({
    queryFn: () => authedV3Request(`v3/admin/users/${id}`, authToken),
    queryKey: ["admin", "user", id, authToken],
    retry: 0,
    staleTime: 30 * 1000,
    enabled: !!authToken && !!id,
  });
};

/*
 * Paginated GIF session list. Response is the same pagination envelope as users:
 *   { current_page, rows_per_page, page_count, data: [...] }
 * Each row + the detail use ADMIN_GET_GIF_SHAPE. Verified against
 * Api::V3::Admin::GifsController#index + GifMapper: the mapper emits
 *   { id, approved, channel: { id, name } }
 * — the shape constant lists `device_name` but GifMapper has no mapping for it,
 * so it is NOT returned (don't render a device column expecting it). The index
 * accepts ONLY page/per_page — there are no search/status filter params on the
 * endpoint, so the monitor is pagination-only until the API adds them.
 */
export const useAdminGifs = ({ page } = {}) => {
  const authToken = useSelector(selectAuthToken());
  // `!= null` (not truthiness) so an explicit page 0 is sent verbatim rather
  // than dropped — matches buildUsersQuery's guard.
  const qs = page != null ? `?page=${page}` : "";
  return useQuery({
    queryFn: () => authedV3Request(`v3/admin/gifs${qs}`, authToken),
    queryKey: ["admin", "gifs", page, authToken],
    retry: 0,
    staleTime: 30 * 1000,
    keepPreviousData: true,
    enabled: !!authToken,
  });
};

/*
 * Single GIF detail — ADMIN_GET_GIF_SHAPE ({ id, approved, channel: {id, name} }).
 * Verified against Api::V3::Admin::GifsController#show.
 */
export const useAdminGif = (id) => {
  const authToken = useSelector(selectAuthToken());
  return useQuery({
    queryFn: () => authedV3Request(`v3/admin/gifs/${id}`, authToken),
    queryKey: ["admin", "gif", id, authToken],
    retry: 0,
    staleTime: 30 * 1000,
    enabled: !!authToken && !!id,
  });
};

// per_page for the device list — also the page-full threshold the monitor uses
// to decide whether a "next page" exists (the endpoint returns no pagination
// metadata, so a full page is the only signal that more rows may follow).
export const DEVICE_STATUSES_PER_PAGE = 25;

/*
 * Device-status list. UNLIKE users/gifs, this endpoint returns a BARE ARRAY
 * (no { current_page, page_count, data } envelope) of
 * V2ApiConstants::DEVICE_STATUSES_DEFAULT_RESPONSE_SHAPE items — battery / iOS /
 * queue / upload telemetry per device (note the camelCase fields like
 * gifsBeingMadeInProgress). Verified against
 * Api::V3::Admin::DeviceStatusesController#index. The server paginates via
 * page/per_page but emits no page metadata, so the monitor pages with a
 * next/prev "is this page full?" heuristic rather than a numbered paginator.
 * `per_page` is pinned so the heuristic is well-defined.
 */
export const useAdminDeviceStatuses = ({ page } = {}) => {
  const authToken = useSelector(selectAuthToken());
  const params = new URLSearchParams();
  if (page != null) params.set("page", String(page));
  params.set("per_page", String(DEVICE_STATUSES_PER_PAGE));
  return useQuery({
    queryFn: () =>
      authedV3Request(`v3/admin/device-statuses?${params.toString()}`, authToken),
    queryKey: ["admin", "deviceStatuses", page, authToken],
    retry: 0,
    staleTime: 30 * 1000,
    keepPreviousData: true,
    enabled: !!authToken,
  });
};

/*
 * Single device-status detail — same DEVICE_STATUSES_DEFAULT_RESPONSE_SHAPE.
 * Verified against Api::V3::Admin::DeviceStatusesController#show.
 */
export const useAdminDeviceStatus = (id) => {
  const authToken = useSelector(selectAuthToken());
  return useQuery({
    queryFn: () => authedV3Request(`v3/admin/device-statuses/${id}`, authToken),
    queryKey: ["admin", "deviceStatus", id, authToken],
    retry: 0,
    staleTime: 30 * 1000,
    enabled: !!authToken && !!id,
  });
};

/*
 * Current global terms HTML. Reads the AUTHED admin endpoint
 * GET /api/v3/admin/settings → **{ camber_terms }** (Api::V3::Admin::SettingsController)
 * — the value is RAW, UN-STRIPPED HTML (the full camber_terms document). The
 * admin editor seeds its textarea from `camber_terms` and writes back via the
 * authed PUT /api/v3/admin/settings mutation (same response shape), so the
 * round-trip preserves HTML tags exactly. The public GET /settings/terms
 * (→ `{ terms }` with strip_tags applied) is the public read; it is unchanged
 * and no longer used by the editor. Gated on authToken so a tokenless mount
 * doesn't fire a 401.
 */
export const useAdminTerms = () => {
  const authToken = useSelector(selectAuthToken());
  return useQuery({
    queryFn: () => authedV3Request("v3/admin/settings", authToken, { method: "GET" }),
    queryKey: ["admin", "terms", authToken],
    retry: 0,
    staleTime: 30 * 1000,
    enabled: !!authToken,
  });
};

/*
 * Global share-groups list + platform share metrics. Response (verified against
 * Api::V3::Admin::ShareGroupsController#index):
 *   { current_page, rows_per_page (75), page_count,
 *     data: [{ id, gif_id, created_at, share_count, success_count, fail_count,
 *              pending_count }],
 *     metrics: { daily_success_rate, weekly_success_rate, monthly_success_rate,
 *                all_time_success_rate, daily_execution_avg,
 *                monthly_execution_avg } }
 * Params: page (server pins per_page at 75). keepPreviousData so paging doesn't
 * flash empty.
 */
export const useAdminShareGroups = ({ page } = {}) => {
  const authToken = useSelector(selectAuthToken());
  const qs = page != null ? `?page=${page}` : "";
  return useQuery({
    queryFn: () => authedV3Request(`v3/admin/share-groups${qs}`, authToken),
    queryKey: ["admin", "shareGroups", page, authToken],
    retry: 0,
    staleTime: 30 * 1000,
    keepPreviousData: true,
    enabled: !!authToken,
  });
};
